import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import {
	ApiResponseSchema,
} from '../schemas';
import { ProvinceSchema } from './provinceApi';
import { NivoLineChartDataSchema } from '../schemas/chartSchema';

export const Top149SchoolSchema = z.object({
	maTruong: z.string(),
	// Vắng mặt/null cho trường chỉ biết nhóm sau sáp nhập (chưa biết tỉnh gốc) —
	// xem docs/dev-guide/truong-thpt-va-nhom-tinh-sap-nhap.md.
	maTinh: z.string().optional(),
	nhomSapNhap: z.string().optional(),
	maNoiBo: z.string().nullable(),
	tenTruong: z.string().transform(val => (val === "NULL" || val === "#Chưa xác định") ? "#Chưa xác định" : val),
	top149: z.boolean(),
	ghiChu: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	tinhThanh: ProvinceSchema.nullable().optional(),
}).strip();

export const Top149SchoolYearStatsSchema = z.object({
	maTruong: z.string(),
	tenTruong: z.string(),
	ghiChu: z.string().nullable(),
	tenTinh: z.string().optional(),
	tenTinhSauSapNhap: z.string().optional(),
	maTruongSauSapNhap: z.string().optional(),
	hasCodeNameChange: z.boolean().optional(),
	yearData: z.record(z.string(), z.number())
}).strip();

export const Top149SchoolStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(Top149SchoolYearStatsSchema),
	total: z.number().optional()
}).strip();

export const Top149ProvinceQueryParamsSchema = z.object({
	year: z.number(),
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	maTinh: z.string().optional(),
	tenTinh: z.string().optional()
}).strip();

export const Top149ProvinceYearStatsSchema = z.object({
	maTinh: z.string(),
	tenTinh: z.string(),
	soTruong: z.number(),
	yearData: z.record(z.string(), z.number())
}).strip();

export const Top149ProvinceStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(Top149ProvinceYearStatsSchema),
	total: z.number().optional()
}).strip();

/**
 * Build URLSearchParams from Top149ProvinceQueryParams
 */
function buildProvinceParams(params: z.infer<typeof Top149ProvinceQueryParamsSchema>): URLSearchParams {
	const queryParams = new URLSearchParams();
	queryParams.append('year', params.year.toString());
	if (params.sortBy) queryParams.append('sortBy', params.sortBy);
	if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
	if (params.maTinh) queryParams.append('maTinh', params.maTinh);
	if (params.tenTinh) queryParams.append('tenTinh', params.tenTinh);
	return queryParams;
}

export const top149Api = {
	async getTop149Stats(params?: URLSearchParams) {
		const url = buildApiUrl('/top149/stats/admission', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(Top149SchoolStatsResponseSchema).parse(response.data);
	},

	async getNivoLineChartData(params?: URLSearchParams) {
		const url = buildApiUrl('/top149/stats/nivo-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	async exportToExcel(params?: URLSearchParams): Promise<void> {
		return apiClient.exportToExcel('/top149/export/excel', params);
	},

	async getTop149ByProvince(params: z.infer<typeof Top149ProvinceQueryParamsSchema>, merged = false) {
		const queryParams = buildProvinceParams(params);
		const endpoint = merged ? '/top149/stats/by-province-merged' : '/top149/stats/by-province';
		const url = buildApiUrl(endpoint, queryParams);
		const response = await apiClient.get(url);
		return ApiResponseSchema(Top149ProvinceStatsResponseSchema).parse(response.data);
	},

	async exportProvinceStatsToExcel(
		params: z.infer<typeof Top149ProvinceQueryParamsSchema>,
		merged = false
	): Promise<void> {
		const queryParams = buildProvinceParams(params);
		const endpoint = merged
			? '/top149/stats/by-province-merged/export/excel'
			: '/top149/stats/by-province/export/excel';
		return apiClient.exportToExcel(endpoint, queryParams);
	},
};